import React, { useEffect, useState } from "react";
import TablePaginationComponents from "../components/TablePaginationComponents";
import CommonModal from "../components/CommonModal";
import {
  getWhatsappLogs,
  getWhatsappUserMessages,
} from "../../Services/Service";

function WhatsappLogs() {
  /* =======================
     Main Table State
  ======================= */
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    total_records: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    previous_page: null,
    next_page: null,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  /* =======================
     Modal State
  ======================= */
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [logData, setLogData] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const [logPagination, setLogPagination] = useState({
    total_records: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    previous_page: null,
    next_page: null,
  });

  /* =======================
     Load Main Table
  ======================= */
  useEffect(() => {
    loadData();
  }, [pagination.current_page, pagination.per_page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      loadData(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadData = async (search = searchTerm) => {
    setLoading(true);
    try {
      const res = await getWhatsappLogs(
        pagination.current_page,
        pagination.per_page,
        search
      );
      setData(res?.data?.data?.records ?? []);
      setPagination((prev) => res?.data?.data?.pagination ?? prev);
    } catch (err) {
      console.error(err);
      setData([]);
    }
    setLoading(false);
  }; 

  /* =======================
     Load User Messages
  ======================= */
  const loadUserMessages = async (
    userId,
    page = logPagination.current_page,
    perPage = logPagination.per_page
  ) => {
    setLogLoading(true);
    try {
      const res = await getWhatsappUserMessages(userId, page, perPage);
      setLogData(res?.data?.data?.records ?? []);
      setLogPagination(res?.data?.data?.pagination ?? logPagination);
    } catch (err) {
      console.error(err);
      setLogData([]);
    }
    setLogLoading(false);
  };

  const openMessageModal = (userId) => {
    setSelectedUserId(userId);
    setShowModal(true);
    loadUserMessages(userId, 1, logPagination.per_page);
  };

  const closeModal = () => {
    setShowModal(false);
    setLogData([]);
  };

  /* =======================
     Columns
  ======================= */
  const columns = [
    {
      label: "#",
      render: (_, index) =>
        (pagination.current_page - 1) * pagination.per_page + index + 1,
    },
    { label: "Full Name", key: "fullName" },
    { label: "NHS Number", key: "nhsNumber" },
    { label: "Mobile", key: "mobile" },
    { label: "Email", key: "email" },
    { label: "Coach", key: "coachName" },
    { label: "Coach Assign", key: "coachingStartDate" },
    { label: "Registration Date", key: "registrationDate" },
    { label: "Onboarded Date", key: "onboardedDate" },
    {
      label: "View Message",
      render: (row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => openMessageModal(row.userId)}
        >
          View Message
        </button>
      ),
    },
  ];

  const messageColumns = [
    { label: "#", render: (_, i) => i + 1 },
    { label: "Message", key: "conversation" },
    { label: "Status", key: "message_status" },
    { label: "Sent At", key: "statusTime" },
  ];

  return (
    <>
      {/* =======================
          Main Table
      ======================= */}
      <section className="projects-section uploadtab">
        <div className="d-flex align-items-center mb-3 justify-content-end">
          <div className="input-group" style={{ maxWidth: 320 }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by mobile number..."
              value={searchTerm}
              inputMode="numeric"
              maxLength={15}
              onChange={(e) => setSearchTerm(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPagination((prev) => ({ ...prev, current_page: 1 }));
                  loadData(e.target.value.replace(/\D/g, ''));
                }
              }}
            />
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={() => {
                setSearchTerm('');
                setPagination((prev) => ({ ...prev, current_page: 1 }));
                loadData('');
              }}
            >
              Clear
            </button>
          </div>
        </div>
        <TablePaginationComponents
          columns={columns}
          data={data}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, current_page: page }))
          }
          onRowsPerPageChange={(n) =>
            setPagination((prev) => ({
              ...prev,
              per_page: n,
              current_page: 1,
            }))
          }
        />
      </section>

      {/* =======================
          Modal with Pagination
      ======================= */}
      <CommonModal
        show={showModal}
        onClose={closeModal}
        title="Whatsapp Messages"
        size="xl"
      >
        <TablePaginationComponents
          columns={messageColumns}
          data={logData}
          loading={logLoading}
          pagination={logPagination}
          onPageChange={(page) => {
            setLogPagination((prev) => ({ ...prev, current_page: page }));
            loadUserMessages(selectedUserId, page, logPagination.per_page);
          }}
          onRowsPerPageChange={(n) => {
            setLogPagination((prev) => ({
              ...prev,
              per_page: n,
              current_page: 1,
            }));
            loadUserMessages(selectedUserId, 1, n);
          }}
        />
      </CommonModal>
    </>
  );
}

export default WhatsappLogs;
